// client/src/components/VoiceAssistant.js
import React, { useState, useRef, useEffect } from "react";

export default function VoiceAssistant() {
  const [isSupported, setIsSupported] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState("");
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [assistantContext, setAssistantContext] = useState({});

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Check for mic support
  useEffect(() => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setIsSupported(false);
    }
  }, []);

  // Listen for external open trigger (Talk to My Docs button)
  useEffect(() => {
    const openHandler = () => setIsOpen(true);
    window.addEventListener("open-voice-assistant", openHandler);
    return () => window.removeEventListener("open-voice-assistant", openHandler);
  }, []);

  // Listen for context updates from dashboard / property view
  useEffect(() => {
    const handler = (e) => {
      setAssistantContext(e.detail || {});
    };
    window.addEventListener("voice-context-update", handler);
    return () => window.removeEventListener("voice-context-update", handler);
  }, []);

  const startRecording = async () => {
    try {
      setError("");
      setTranscript("");
      setReply("");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        sendAudio(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error(err);
      setError("Mic access failed. Check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsOpen(true);
    }
  };

  const sendAudio = async (audioBlob) => {
    try {
      setIsThinking(true);
      setError("");

      const formData = new FormData();
      formData.append("audio", audioBlob, "voice.webm");
      formData.append("context", JSON.stringify(assistantContext));

      const res = await fetch("/api/voice/query", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(`Server responded ${res.status}`);

      const data = await res.json();
      setTranscript(data.transcript || "");
      setReply(data.reply || "");
    } catch (err) {
      console.error(err);
      setError("Something went wrong sending your voice message.");
    } finally {
      setIsThinking(false);
    }
  };

  const sendText = async () => {
    if (!input.trim()) return;

    const message = input.trim();
    setTranscript(message);
    setReply("");
    setError("");
    setIsOpen(true);
    setIsThinking(true);

    try {
      const res = await fetch("/api/voice/query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: message,
          context: assistantContext,
        }),
      });

      if (!res.ok) throw new Error(`Server responded ${res.status}`);

      const data = await res.json();
      setReply(data.reply || "");
    } catch (err) {
      console.error(err);
      setError("Error sending text message.");
    } finally {
      setIsThinking(false);
      setInput("");
    }
  };

  if (!isSupported) return null;

  return (
    <>
      {/* BOTTOM INPUT BAR */}
      <div className="fixed bottom-0 left-0 right-0 bg-white px-4 py-3 shadow-lg z-50 flex items-center gap-3">
        <input
          type="text"
          className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-gray-800 text-sm"
          placeholder="Ask Harbour Nest anything…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendText()}
        />

        {/* Mic button */}
        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`w-10 h-10 flex items-center justify-center rounded-full shadow 
          ${isRecording ? "bg-red-600" : "bg-blue-600"} text-white`}
        >
          {isRecording ? "⏹" : "🎤"}
        </button>
      </div>

      {/* SLIDE-UP ASSISTANT PANEL */}
      {isOpen && (
        <div className="fixed bottom-16 right-4 w-80 max-w-[90vw] bg-slate-900 text-slate-100 rounded-2xl shadow-xl p-4 z-40 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold">Harbour Nest Assistant</h3>
            <button
              className="text-xs text-slate-400 hover:text-white"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          {isRecording && (
            <div className="text-xs text-amber-300">Listening… tap to stop.</div>
          )}

          {isThinking && (
            <div className="text-xs text-blue-300 animate-pulse">Thinking…</div>
          )}

          {error && (
            <div className="text-xs text-red-400">{error}</div>
          )}

          {transcript && (
            <div className="text-xs">
              <div className="font-semibold text-slate-300 mb-1">You said:</div>
              <div className="bg-slate-800 p-2 rounded-xl">{transcript}</div>
            </div>
          )}

          {reply && (
            <div className="text-xs">
              <div className="font-semibold text-slate-300 mb-1">Harbour Nest:</div>
              <div className="bg-slate-800 p-2 rounded-xl max-h-48 overflow-y-auto whitespace-pre-wrap">
                {reply}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
